import _ from 'underscore';
import Backbone from "backbone";
import VoiceActivityDetectionHelper from "models/audioEditor/VoiceActivityDetectionHelper";
import RnnoiseVoiceActivityDetectionHelper from "models/audioEditor/RnnoiseVoiceActivityDetectionHelper";

// VAD Provider Constants
const VAD_PROVIDERS = {
    RNNOISE: 'rnnoise',
    SILERO: 'silero'
};

// Global switch - change this to switch providers
const ACTIVE_VAD_PROVIDER = VAD_PROVIDERS.RNNOISE;

// Configuration types for different use cases
const VAD_CONFIGS = {
    SILENCE_DETECTION: 'silence_detection',
    WORD_BOUNDARY: 'word_boundary',
    BALANCED: 'balanced'
};

const VoiceActivityDetectionManager = Backbone.Model.extend({
    idAttribute: "id",

    constructor: function(attributes, options) {
        Backbone.Model.apply(this, [attributes, options]);
    },

    /**
     * Get configuration constants
     */
    getVadProviders: function() {
        return VAD_PROVIDERS;
    },

    getVadConfigs: function() {
        return VAD_CONFIGS;
    },

    getActiveVadProvider: function() {
        return ACTIVE_VAD_PROVIDER;
    },

    /**
     * Get appropriate detector based on active provider
     */
    getDetector: function(configType) {
        if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.RNNOISE) {
            return RnnoiseVoiceActivityDetectionHelper.getInstance();
        } else {
            return VoiceActivityDetectionHelper.getInstance();
        }
    },

    /**
     * Map configuration types to RNNoise thresholds
     */
    getThresholdForConfig: function(configType) {
        const thresholds = {
            [VAD_CONFIGS.SILENCE_DETECTION]: 0.8,  // High threshold for aggressive silence detection
            [VAD_CONFIGS.WORD_BOUNDARY]: 0.3,      // Low threshold for sensitive detection around words
            [VAD_CONFIGS.BALANCED]: 0.5            // Default balanced threshold
        };
        return thresholds[configType] || 0.5;
    },

    /**
     * Unified detect method that works with both providers
     * @param {AudioBuffer} audioBuffer - The audio to analyze
     * @param {string} configType - Configuration type (SILENCE_DETECTION, WORD_BOUNDARY, BALANCED)
     * @param {function} onProgress - Progress callback function
     * @returns {Promise<Array>} VAD results
     */
    detect: async function(audioBuffer, configType = VAD_CONFIGS.BALANCED, onProgress) {
        try {
            if (process.env.NODE_ENV === 'development') {
                console.log(`[VAD Manager] Starting detection with provider: ${ACTIVE_VAD_PROVIDER}, config: ${configType}`);
            }
            const detector = this.getDetector(configType);

            if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.RNNOISE) {
                if (process.env.NODE_ENV === 'development') {
                    console.log('[VAD Manager] Using RNNoise VAD');
                }
                const threshold = this.getThresholdForConfig(configType);
                return await detector.processAudioBuffer(audioBuffer, threshold, onProgress);
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.log('[VAD Manager] Using Silero VAD');
                }
                if (detector.detectWithConfig) {
                    return await detector.detectWithConfig(audioBuffer, configType);
                } else {
                    console.warn('[VAD Manager] detectWithConfig not available, falling back to default detect');
                    return await detector.detect(audioBuffer);
                }
            }
        } catch (error) {
            console.error(`[VAD Manager] Detection failed with provider ${ACTIVE_VAD_PROVIDER}:`, error);

            // If Silero VAD fails, try falling back to RNNoise as a last resort
            if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.SILERO) {
                console.warn('[VAD Manager] Silero VAD failed, attempting fallback to RNNoise');
                try {
                    const rnnoiseDetector = RnnoiseVoiceActivityDetectionHelper.getInstance();
                    const threshold = this.getThresholdForConfig(configType);
                    const fallbackResult = await rnnoiseDetector.processAudioBuffer(audioBuffer, threshold, onProgress);
                    if (process.env.NODE_ENV === 'development') {
                        console.log('[VAD Manager] Fallback to RNNoise succeeded');
                    }
                    return fallbackResult;
                } catch (fallbackError) {
                    console.error('[VAD Manager] Fallback to RNNoise also failed:', fallbackError);
                    throw new Error(`Both VAD providers failed. Silero: ${error.message}, RNNoise: ${fallbackError.message}`);
                }
            }

            throw error;
        }
    },

    /**
     * Check if voice activity data contains voice activity in a time range
     * Uses the appropriate helper based on active provider
     */
    containsVoiceActivity: function(voiceActivityDetectionResultArray, startTimeInMilliseconds, endTimeInMilliseconds) {
        if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.RNNOISE) {
            // RNNoise format compatibility - assume same format as Silero for now
            return VoiceActivityDetectionHelper.getInstance().containsVoiceActivity(
                voiceActivityDetectionResultArray, 
                startTimeInMilliseconds, 
                endTimeInMilliseconds
            );
        } else {
            return VoiceActivityDetectionHelper.getInstance().containsVoiceActivity(
                voiceActivityDetectionResultArray, 
                startTimeInMilliseconds, 
                endTimeInMilliseconds
            );
        }
    },

    /**
     * Get storage key for caching VAD results
     */
    getStorageKey: function(configType) {
        return `${ACTIVE_VAD_PROVIDER}_${configType}`;
    },

    /**
     * Get configuration description for debugging/logging
     */
    getConfigDescription: function(configType) {
        const descriptions = {
            [VAD_CONFIGS.SILENCE_DETECTION]: "Optimized for detecting pure silence (aggressive)",
            [VAD_CONFIGS.WORD_BOUNDARY]: "Optimized for detecting speech around word boundaries (sensitive)",
            [VAD_CONFIGS.BALANCED]: "Balanced configuration for general use"
        };
        return descriptions[configType] || "Unknown configuration";
    },
    
    /**
     * Reset VAD initialization failures and allow retry
     */
    resetVadInitialization: function() {
        if (process.env.NODE_ENV === 'development') {
            console.log('[VAD Manager] Resetting VAD initialization');
        }
        if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.SILERO) {
            VoiceActivityDetectionHelper.getInstance().resetInitializationFailures();
        }
        // RNNoise doesn't typically have initialization failures that need resetting
    },
    
    /**
     * Get VAD status for debugging
     */
    getVadStatus: function() {
        const status = {
            activeProvider: ACTIVE_VAD_PROVIDER,
            availableProviders: VAD_PROVIDERS,
            availableConfigs: VAD_CONFIGS
        };
        
        if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.SILERO) {
            status.sileroStatus = VoiceActivityDetectionHelper.getInstance().getInitializationStatus();
        }
        
        return status;
    },
    
    /**
     * Attempt to reinitialize VAD after failures
     */
    reinitializeVad: async function() {
        if (process.env.NODE_ENV === 'development') {
            console.log('[VAD Manager] Attempting to reinitialize VAD');
        }
        this.resetVadInitialization();

        // Try to create a simple detector instance to test if VAD is working
        try {
            if (ACTIVE_VAD_PROVIDER === VAD_PROVIDERS.SILERO) {
                await VoiceActivityDetectionHelper.getInstance().getVoiceActivityDetectorWithConfig('BALANCED');
                if (process.env.NODE_ENV === 'development') {
                    console.log('[VAD Manager] Silero VAD reinitialization successful');
                }
            } else {
                // For RNNoise, we don't need to pre-initialize
                if (process.env.NODE_ENV === 'development') {
                    console.log('[VAD Manager] RNNoise VAD ready');
                }
            }
            return true;
        } catch (error) {
            console.error('[VAD Manager] VAD reinitialization failed:', error);
            return false;
        }
    }
});

const VoiceActivityDetectionManagerInstance = new VoiceActivityDetectionManager();

export default {
    getInstance: function() {
        return VoiceActivityDetectionManagerInstance;
    },
    
    // Export constants for easy access
    VAD_PROVIDERS: VAD_PROVIDERS,
    VAD_CONFIGS: VAD_CONFIGS,
    ACTIVE_VAD_PROVIDER: ACTIVE_VAD_PROVIDER,
    
    // Utility methods for debugging
    getStatus: function() {
        return VoiceActivityDetectionManagerInstance.getVadStatus();
    },
    
    resetAndRetry: async function() {
        return await VoiceActivityDetectionManagerInstance.reinitializeVad();
    }
};